import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, ListGroup, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    FaBuilding,
    FaClock,
    FaFacebook,
    FaInstagram,
    FaMapMarkerAlt,
    FaMoneyBillWave,
    FaPlus,
    FaShareAlt,
    FaTiktok,
    FaTrash,
    FaTruck,
    FaWhatsapp
} from 'react-icons/fa';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import { useGetCompanyByRucQuery, useUpdateCompanyMutation } from '../../slices/companyApiSlice';
import { useDeleteImageMutation } from '../../slices/utilsApiSlice';
import { compressAndUploadImage } from '../../utils/compressAndUploadImage';
import { resolveGoogleMapsUrl } from '../../utils/googleMapsUrl';
import provinceCantonData from '../../data/ecuador_provinces_cantons_labeled.json';
import ImageCropper from '../../components/ui/ImageCropper';

const cantonCatalog = Object.entries(provinceCantonData).flatMap(([province, rows]) =>
    (rows || []).map((row) => ({
        city: Object.keys(row || {})[0],
        state: province,
    })).filter((item) => item.city)
);

const findCanton = (city) => {
    const trimmed = (city || '').trim().toLowerCase();
    if (!trimmed) return null;
    return cantonCatalog.find((item) => item.city.toLowerCase() === trimmed) || null;
};

const emptyPickupLocation = () => ({
    clientKey: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: '',
    address: '',
    city: '',
    state: '',
    extraHours: 0,
    phoneNumber: '',
    googleMapsUrl: '',
    isActive: true,
});

const hasCoordinate = (value) => value !== '' && value != null && Number.isFinite(Number(value));

const toPickupPayload = (location) => {
    const extraHours = Number(location?.extraHours);
    const canton = findCanton(location?.city);
    const payload = {
        address: (location?.address || '').trim(),
        city: canton?.city || (location?.city || '').trim(),
        extraHours: Number.isFinite(extraHours) && extraHours >= 0 ? extraHours : 0,
        isActive: location?.isActive !== false,
    };

    const name = (location?.name || '').trim();
    const state = (location?.state || '').trim() || canton?.state || '';
    const phoneNumber = (location?.phoneNumber || '').trim();
    const googleMapsUrl = (location?.googleMapsUrl || '').trim();

    if (name) payload.name = name;
    if (state) payload.state = state;
    if (phoneNumber) payload.phoneNumber = phoneNumber;
    if (googleMapsUrl) payload.googleMapsUrl = googleMapsUrl;
    if (location?._id) payload._id = location._id;

    const lat = location?.coordinates?.lat;
    const lng = location?.coordinates?.lng;
    if (hasCoordinate(lat) && hasCoordinate(lng)) {
        payload.coordinates = { lat: Number(lat), lng: Number(lng) };
    }

    return payload;
};

const mapPickupLocationFromCompany = (location) => ({
    _id: location?._id,
    clientKey: location?._id ? String(location._id) : emptyPickupLocation().clientKey,
    name: location?.name || '',
    address: location?.address || '',
    city: location?.city || '',
    state: location?.state || '',
    extraHours: location?.extraHours ?? 0,
    phoneNumber: location?.phoneNumber || '',
    googleMapsUrl: location?.googleMapsUrl || '',
    coordinates: location?.coordinates
        ? {
            lat: location.coordinates.lat ?? '',
            lng: location.coordinates.lng ?? '',
        }
        : undefined,
    isActive: location?.isActive !== false,
});

const pickupLocationLabel = (location, index) => {
    const name = (location?.name || '').trim();
    return name ? `"${name}"` : `número ${index + 1}`;
};

const SHIPPING_POLICY_OPTIONS = [
    {
        value: 'NO_FREE_SHIPPING',
        title: 'Sin envío gratis',
        description: 'El costo de envío se calcula al finalizar la compra según la ubicación.',
    },
    {
        value: 'FREE_OVER_AMOUNT',
        title: 'Envío gratis por monto mínimo',
        description: 'El envío es gratis cuando la compra supera el monto indicado.',
    },
];

const validateAdditionalPickupLocations = (locations = []) => {
    for (let index = 0; index < locations.length; index += 1) {
        const location = locations[index];
        const label = pickupLocationLabel(location, index);

        if (!(location?.address || '').trim()) {
            return `Ingresa la dirección del punto de retiro ${label}.`;
        }
        if (!(location?.city || '').trim()) {
            return `Ingresa la ciudad del punto de retiro ${label}.`;
        }
        if (!findCanton(location.city)) {
            return `La ciudad del punto de retiro ${label} debe ser el cantón, igual que la ciudad de la empresa. Por ejemplo: Cuenca o Gualaceo.`;
        }

        const extraHours = Number(location?.extraHours);
        if (location?.extraHours === '' || !Number.isFinite(extraHours) || extraHours < 0) {
            return `Las horas adicionales del punto de retiro ${label} deben ser un número igual o mayor a 0.`;
        }
    }

    return '';
};

const CompanyEditScreen = ({ ruc, onClose, refetch }) => {
    const { data: company, isLoading, error } = useGetCompanyByRucQuery(ruc);
    const [updateCompany, { isLoading: loadingUpdate }] = useUpdateCompanyMutation();
    const [deleteImage] = useDeleteImageMutation();

    const [formData, setFormData] = useState({
        ruc: '',
        legalName: '',
        tradeName: '',
        description: '',
        phoneNumber: '',
        logoUrl: '',
        city: '',
        shippingPolicy: {
            type: 'NO_FREE_SHIPPING',
            freeShippingThreshold: '',
        },
        paymentMethods: [],
        isActive: false,
        location: {
            address: '',
            googleMapsUrl: '',
            coordinates: {
                lat: 0,
                lng: 0,
            },
        },
        openingHours: [],
        googlePlaceId: '',
        socialMedia: {
            whatsapp: '',
            instagram: '',
            facebook: '',
            tiktok: '',
        },
        additionalPickupLocations: [],
    });

    const [newOpeningHour, setNewOpeningHour] = useState('');
    const [uploading, setUploading] = useState(false);
    const [showCropper, setShowCropper] = useState(false);
    const [rawImageSrc, setRawImageSrc] = useState(null);
    const [loadingMapsUrl, setLoadingMapsUrl] = useState(false);
    const [loadingPickupMapsIndex, setLoadingPickupMapsIndex] = useState(null);

    useEffect(() => {
        if (company) {
            setFormData({
                ruc: company.ruc,
                legalName: company.legalName || '',
                tradeName: company.tradeName || '',
                description: company.description || '',
                phoneNumber: company.phoneNumber || '',
                logoUrl: company.logoUrl || '',
                city: company.city || '',
                shippingPolicy: {
                    type: company.shippingPolicy?.type || 'NO_FREE_SHIPPING',
                    freeShippingThreshold: company.shippingPolicy?.freeShippingThreshold || '',
                },
                paymentMethods: company.paymentMethods ? [...company.paymentMethods] : [],
                isActive: company.isActive || false,
                location: {
                    address: company.location?.address || '',
                    googleMapsUrl: company.location?.googleMapsUrl || '',
                    coordinates: {
                        lat: company.location?.coordinates?.lat || 0,
                        lng: company.location?.coordinates?.lng || 0,
                    },
                },
                openingHours: company.openingHours ? [...company.openingHours] : [],
                googlePlaceId: company.googlePlaceId || '',
                socialMedia: {
                    whatsapp: company.socialMedia?.whatsapp || '',
                    instagram: company.socialMedia?.instagram || '',
                    facebook: company.socialMedia?.facebook || '',
                    tiktok: company.socialMedia?.tiktok || '',
                },
                additionalPickupLocations: Array.isArray(company.additionalPickupLocations)
                    ? company.additionalPickupLocations.map(mapPickupLocationFromCompany)
                    : [],
            });
        }
    }, [company]);

    const applyResolvedPlace = (prev, resolved) => {
        let description = prev.description || '';
        if (resolved.editorialSummary) {
            description = resolved.editorialSummary;
        }
        if (resolved.phoneNumber && !description.includes(resolved.phoneNumber)) {
            const phoneText = `Tel: ${resolved.phoneNumber}`;
            description = description ? `${description}\n${phoneText}` : phoneText;
        }
        if (resolved.websiteURI && !description.includes(resolved.websiteURI)) {
            const webText = `Web: ${resolved.websiteURI}`;
            description = description ? `${description}\n${webText}` : webText;
        }

        const name = resolved.name || '';
        const lat = Number.isFinite(Number(resolved.lat)) ? Number(resolved.lat) : prev.location.coordinates.lat;
        const lng = Number.isFinite(Number(resolved.lng)) ? Number(resolved.lng) : prev.location.coordinates.lng;

        return {
            ...prev,
            tradeName: name || prev.tradeName,
            legalName: !prev.legalName && name ? name : prev.legalName,
            description,
            phoneNumber: resolved.phoneNumber || prev.phoneNumber,
            city: resolved.city || prev.city,
            location: {
                ...prev.location,
                address: resolved.address || prev.location.address,
                coordinates: { lat, lng },
                googleMapsUrl: resolved.googleMapsUrl || prev.location.googleMapsUrl,
            },
            googlePlaceId: resolved.googlePlaceId || prev.googlePlaceId,
            openingHours: resolved.openingHours?.length ? resolved.openingHours : prev.openingHours,
        };
    };

    const handleFillFromMapsUrl = async () => {
        const url = formData.location.googleMapsUrl?.trim();
        if (!url) {
            toast.error('Pega el enlace de Google Maps.');
            return;
        }

        setLoadingMapsUrl(true);
        try {
            const resolved = await resolveGoogleMapsUrl(url);
            setFormData((prev) => applyResolvedPlace(prev, resolved));
            toast.success('Información actualizada desde Google Maps');
        } catch (error) {
            toast.error(error?.message || 'No se pudo leer el enlace de Google Maps.');
        } finally {
            setLoadingMapsUrl(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith('location.')) {
            const field = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                location: {
                    ...prev.location,
                    [field]: value,
                },
            }));
        } else if (name.startsWith('shippingPolicy.')) {
            const field = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                shippingPolicy: {
                    ...prev.shippingPolicy,
                    [field]:
                        field === 'freeShippingThreshold'
                            ? value === ''
                                ? ''
                                : Number(value)
                            : value,
                },
            }));
        } else if (name.startsWith('socialMedia.')) {
            const field = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                socialMedia: {
                    ...prev.socialMedia,
                    [field]: value,
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        }
    };

    const handlePaymentMethodsChange = (e) => {
        const { value, checked } = e.target;
        setFormData((prev) => {
            const currentMethods = prev.paymentMethods || [];
            if (checked) {
                return { ...prev, paymentMethods: [...currentMethods, value] };
            } else {
                return {
                    ...prev,
                    paymentMethods: currentMethods.filter((method) => method !== value),
                };
            }
        });
    };

    const handleAddOpeningHour = () => {
        if (newOpeningHour.trim()) {
            setFormData((prev) => ({
                ...prev,
                openingHours: [...prev.openingHours, newOpeningHour.trim()],
            }));
            setNewOpeningHour('');
        }
    };

    const handleRemoveOpeningHour = (index) => {
        setFormData((prev) => ({
            ...prev,
            openingHours: prev.openingHours.filter((_, i) => i !== index),
        }));
    };

    const handlePickupLocationChange = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            additionalPickupLocations: prev.additionalPickupLocations.map((location, locationIndex) => {
                if (locationIndex !== index) return location;
                if (field === 'isActive') {
                    return { ...location, isActive: Boolean(value) };
                }
                if (field === 'extraHours') {
                    return { ...location, extraHours: value };
                }
                return { ...location, [field]: value };
            }),
        }));
    };

    const handleAddPickupLocation = () => {
        setFormData((prev) => ({
            ...prev,
            additionalPickupLocations: [...(prev.additionalPickupLocations || []), emptyPickupLocation()],
        }));
    };

    const handleFillPickupFromMapsUrl = async (index) => {
        const url = formData.additionalPickupLocations?.[index]?.googleMapsUrl?.trim();
        if (!url) {
            toast.error('Pega el enlace de Google Maps.');
            return;
        }
        if (loadingPickupMapsIndex !== null) return;

        setLoadingPickupMapsIndex(index);
        try {
            const resolved = await resolveGoogleMapsUrl(url);
            setFormData((prev) => ({
                ...prev,
                additionalPickupLocations: prev.additionalPickupLocations.map((location, locationIndex) => {
                    if (locationIndex !== index) return location;

                    const lat = Number.isFinite(Number(resolved.lat))
                        ? Number(resolved.lat)
                        : location.coordinates?.lat;
                    const lng = Number.isFinite(Number(resolved.lng))
                        ? Number(resolved.lng)
                        : location.coordinates?.lng;
                    const canton = findCanton(resolved.city);
                    const next = {
                        ...location,
                        name: resolved.name || location.name,
                        address: resolved.address || location.address,
                        city: canton?.city || resolved.city || location.city,
                        state: canton?.state || resolved.state || location.state,
                        phoneNumber: resolved.phoneNumber || location.phoneNumber,
                        googleMapsUrl: resolved.googleMapsUrl || location.googleMapsUrl,
                    };

                    if (hasCoordinate(lat) && hasCoordinate(lng)) {
                        next.coordinates = { lat: Number(lat), lng: Number(lng) };
                    }

                    return next;
                }),
            }));
            toast.success('Punto de retiro actualizado desde Google Maps');
        } catch (error) {
            toast.error(error?.message || 'No se pudo leer el enlace de Google Maps.');
        } finally {
            setLoadingPickupMapsIndex((current) => (current === index ? null : current));
        }
    };

    const handleRemovePickupLocation = (index) => {
        const location = formData.additionalPickupLocations?.[index];
        const label = pickupLocationLabel(location, index);
        if (!window.confirm(`¿Seguro que deseas quitar el punto de retiro ${label}?`)) return;

        setFormData((prev) => ({
            ...prev,
            additionalPickupLocations: prev.additionalPickupLocations.filter(
                (_, locationIndex) => locationIndex !== index
            ),
        }));
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        const pickupError = validateAdditionalPickupLocations(formData.additionalPickupLocations);
        if (pickupError) {
            toast.error(pickupError);
            return;
        }

        const modifiedData = {};
        if (company) {
            const basicFields = [
                'legalName',
                'tradeName',
                'description',
                'phoneNumber',
                'logoUrl',
                'city',
                'isActive',
                'googlePlaceId',
            ];
            basicFields.forEach((key) => {
                if (formData[key] !== company[key]) {
                    modifiedData[key] = formData[key];
                }
            });

            const originalShipping = company.shippingPolicy || {};
            const newShipping = formData.shippingPolicy || {};
            if (
                originalShipping.type !== newShipping.type ||
                originalShipping.freeShippingThreshold !== newShipping.freeShippingThreshold
            ) {
                modifiedData.shippingPolicy = newShipping;
            }

            const originalMethods = company.paymentMethods
                ? [...company.paymentMethods].sort()
                : [];
            const newMethods = formData.paymentMethods ? [...formData.paymentMethods].sort() : [];
            if (JSON.stringify(originalMethods) !== JSON.stringify(newMethods)) {
                modifiedData.paymentMethods = formData.paymentMethods;
            }

            const originalLocation = company.location || {};
            const newLocation = formData.location || {};

            if (
                originalLocation.address !== newLocation.address ||
                originalLocation.googleMapsUrl !== newLocation.googleMapsUrl ||
                originalLocation.coordinates?.lat !== newLocation.coordinates?.lat ||
                originalLocation.coordinates?.lng !== newLocation.coordinates?.lng
            ) {
                modifiedData.location = newLocation;
            }

            const originalHours = company.openingHours ? [...company.openingHours] : [];
            const newHours = formData.openingHours ? [...formData.openingHours] : [];
            if (JSON.stringify(originalHours) !== JSON.stringify(newHours)) {
                modifiedData.openingHours = formData.openingHours;
            }

            const originalSocial = company.socialMedia || {};
            const newSocial = formData.socialMedia || {};
            if (
                originalSocial.whatsapp !== newSocial.whatsapp ||
                originalSocial.instagram !== newSocial.instagram ||
                originalSocial.facebook !== newSocial.facebook ||
                originalSocial.tiktok !== newSocial.tiktok
            ) {
                modifiedData.socialMedia = newSocial;
            }

        }

        const nextPickupLocations = (formData.additionalPickupLocations || []).map(toPickupPayload);
        const originalPickupLocations = Array.isArray(company?.additionalPickupLocations)
            ? company.additionalPickupLocations.map(toPickupPayload)
            : [];
        const pickupChanged = JSON.stringify(originalPickupLocations) !== JSON.stringify(nextPickupLocations);

        if (!pickupChanged && Object.keys(modifiedData).length === 0) {
            toast.info('No hay cambios para guardar');
            onClose();
            return;
        }

        modifiedData.additionalPickupLocations = nextPickupLocations;

        try {
            await updateCompany({ ruc: formData.ruc, data: modifiedData }).unwrap();
            toast.success('Compañía actualizada exitosamente');
            refetch();
            onClose();
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            setRawImageSrc(reader.result);
            setShowCropper(true);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCropComplete = async (croppedImageFile) => {
        const previousLogoUrl = formData.logoUrl;
        setUploading(true);

        try {
            const response = await compressAndUploadImage(croppedImageFile, 'image');
            const urls =
                response.images || (Array.isArray(response.image) ? response.image : [response.image]);
            const nextLogoUrl = urls[0];

            if (!nextLogoUrl) {
                toast.error('No se recibió la URL de la imagen subida');
                return;
            }

            if (previousLogoUrl && previousLogoUrl !== nextLogoUrl) {
                await deleteImage({ imageName: previousLogoUrl }).unwrap();
            }

            setFormData((prevData) => ({
                ...prevData,
                logoUrl: nextLogoUrl,
            }));
            toast.success(response.message || 'Imagen subida correctamente');
        } catch (err) {
            toast.error(err?.data?.message || err.error || err.message || 'Error al subir imagen');
        } finally {
            setUploading(false);
            setShowCropper(false);
            setRawImageSrc(null);
        }
    };

    if (isLoading) return <Loader />;
    if (error)
        return (
            <Message variant="danger">
                {error?.data?.message || error?.error || 'Error al cargar datos'}
            </Message>
        );

    const customColor = '#b5430f';

    return (
        <Form onSubmit={submitHandler} className="p-0">
            <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                    <h5 className="mb-0 d-flex align-items-center" style={{ color: customColor }}>
                        <FaBuilding className="me-2" /> Información General
                    </h5>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group controlId="ruc">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    RUC
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.ruc}
                                    disabled
                                    className="bg-light"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="isActive">
                                <Form.Label className="fw-bold small text-muted text-uppercase d-block" column='sm'>
                                    Estado
                                </Form.Label>
                                <div className="d-flex align-items-center h-100 border rounded px-3 py-2 bg-light">
                                    <Form.Check
                                        type="switch"
                                        id="isActive-switch"
                                        label={formData.isActive ? 'Activa' : 'Inactiva'}
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                        className={
                                            formData.isActive ? 'fw-bold mb-0' : 'text-muted mb-0'
                                        }
                                        style={formData.isActive ? { color: customColor } : {}}
                                    />
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="legalName">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Razón Social
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="legalName"
                                    value={formData.legalName}
                                    onChange={handleChange}
                                    placeholder="Nombre legal de la empresa"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="tradeName">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Nombre Comercial
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="tradeName"
                                    value={formData.tradeName}
                                    onChange={handleChange}
                                    placeholder="Nombre visible al público"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group controlId="description">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Descripción
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Breve descripción de la empresa"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="city">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Ciudad
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder="Ciudad principal"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="phoneNumber">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Teléfono
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                    placeholder="Número de teléfono"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="logoUrl">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Logo
                                </Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={handleImageUpload}
                                    accept="image/*"
                                />
                                {uploading && <Loader />}
                                {formData.logoUrl && (
                                    <div className="mt-2 text-center border rounded p-2 bg-light position-relative">
                                        <img
                                            src={formData.logoUrl}
                                            alt="Logo Preview"
                                            style={{
                                                maxHeight: '100px',
                                                maxWidth: '100%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                    <h5 className="mb-0 d-flex align-items-center" style={{ color: customColor }}>
                        <FaShareAlt className="me-2" /> Redes Sociales
                    </h5>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group controlId="socialMedia.whatsapp">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    <FaWhatsapp className="me-1" /> WhatsApp
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="socialMedia.whatsapp"
                                    value={formData.socialMedia.whatsapp}
                                    onChange={handleChange}
                                    placeholder="Número de WhatsApp"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="socialMedia.instagram">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    <FaInstagram className="me-1" /> Instagram
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="socialMedia.instagram"
                                    value={formData.socialMedia.instagram}
                                    onChange={handleChange}
                                    placeholder="Usuario o URL de Instagram"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="socialMedia.facebook">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    <FaFacebook className="me-1" /> Facebook
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="socialMedia.facebook"
                                    value={formData.socialMedia.facebook}
                                    onChange={handleChange}
                                    placeholder="URL de Facebook"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="socialMedia.tiktok">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    <FaTiktok className="me-1" /> TikTok
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="socialMedia.tiktok"
                                    value={formData.socialMedia.tiktok}
                                    onChange={handleChange}
                                    placeholder="Usuario o URL de TikTok"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                    <h5 className="mb-0 d-flex align-items-center" style={{ color: customColor }}>
                        <FaMapMarkerAlt className="me-2" /> Ubicación
                    </h5>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                    <Row className="g-3">
                        <Col md={12}>
                            <Form.Group controlId="location.googleMapsUrl">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Enlace de Google Maps
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        name="location.googleMapsUrl"
                                        value={formData.location.googleMapsUrl}
                                        onChange={handleChange}
                                        placeholder="https://www.google.com/maps/place/..."
                                    />
                                    <Button
                                        type="button"
                                        variant="primary"
                                        onClick={handleFillFromMapsUrl}
                                        disabled={loadingMapsUrl}
                                        style={{ backgroundColor: customColor, borderColor: customColor }}
                                    >
                                        {loadingMapsUrl ? 'Buscando...' : 'Completar datos'}
                                    </Button>
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Pega el enlace completo del lugar. Si es un enlace corto (maps.app.goo.gl), ábrelo y copia la dirección de la barra del navegador.
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group controlId="location.address">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Dirección
                                </Form.Label>
                                <InputGroup>
                                    <InputGroup.Text className="bg-light">
                                        <FaMapMarkerAlt />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="location.address"
                                        value={formData.location.address}
                                        onChange={handleChange}
                                        placeholder="Dirección completa"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="location.coordinates.lat">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Latitud
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    value={formData.location.coordinates.lat}
                                    disabled
                                    className="bg-light"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="location.coordinates.lng">
                                <Form.Label className="fw-bold small text-muted text-uppercase" column='sm'>
                                    Longitud
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    value={formData.location.coordinates.lng}
                                    disabled
                                    className="bg-light"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <h5 className="mb-0 d-flex align-items-center" style={{ color: customColor }}>
                            <FaMapMarkerAlt className="me-2" /> Puntos de retiro adicionales
                        </h5>
                        <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={handleAddPickupLocation}
                            style={{ backgroundColor: customColor, borderColor: customColor }}
                        >
                            <FaPlus className="me-1" /> Agregar punto de retiro
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                    <p className="text-muted small mb-3">
                        Lugares extra donde el cliente puede retirar el pedido. El local principal se edita arriba.
                    </p>
                    <datalist id="pickup-canton-names">
                        {cantonCatalog.map((item) => (
                            <option key={`${item.state}-${item.city}`} value={item.city} />
                        ))}
                    </datalist>

                    {formData.additionalPickupLocations.length === 0 && (
                        <p className="text-muted text-center fst-italic mb-0">
                            No hay puntos de retiro adicionales.
                        </p>
                    )}

                    {formData.additionalPickupLocations.map((location, index) => {
                        const locationKey = location._id || location.clientKey || index;
                        const isActive = location.isActive !== false;

                        return (
                            <div key={locationKey} className="border rounded p-3 mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="fw-semibold">
                                        {location.name?.trim() || `Punto ${index + 1}`}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="outline-danger"
                                        size="sm"
                                        className="border-0"
                                        onClick={() => handleRemovePickupLocation(index)}
                                        title="Quitar punto de retiro"
                                    >
                                        <FaTrash />
                                    </Button>
                                </div>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <Form.Group controlId={`pickup-maps-${locationKey}`}>
                                            <Form.Label className="fw-bold small text-muted text-uppercase" column="sm">
                                                Enlace de Google Maps
                                            </Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    value={location.googleMapsUrl}
                                                    onChange={(event) =>
                                                        handlePickupLocationChange(index, 'googleMapsUrl', event.target.value)
                                                    }
                                                    placeholder="https://www.google.com/maps/place/..."
                                                />
                                                <Button
                                                    type="button"
                                                    variant="primary"
                                                    onClick={() => handleFillPickupFromMapsUrl(index)}
                                                    disabled={loadingPickupMapsIndex === index}
                                                    style={{ backgroundColor: customColor, borderColor: customColor }}
                                                >
                                                    {loadingPickupMapsIndex === index ? 'Buscando...' : 'Completar datos'}
                                                </Button>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Pega el enlace completo del lugar. Si es corto, ábrelo y copia la dirección de la barra.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId={`pickup-name-${locationKey}`}>
                                            <Form.Label className="fw-bold small text-muted text-uppercase" column="sm">
                                                Nombre
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={location.name}
                                                onChange={(event) =>
                                                    handlePickupLocationChange(index, 'name', event.target.value)
                                                }
                                                placeholder="Nombre del punto (opcional)"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId={`pickup-phone-${locationKey}`}>
                                            <Form.Label className="fw-bold small text-muted text-uppercase" column="sm">
                                                Teléfono
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={location.phoneNumber}
                                                onChange={(event) =>
                                                    handlePickupLocationChange(index, 'phoneNumber', event.target.value)
                                                }
                                                placeholder="Teléfono (opcional)"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group controlId={`pickup-address-${locationKey}`}>
                                            <Form.Label className="fw-bold small text-muted text-uppercase" column="sm">
                                                Dirección
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={location.address}
                                                onChange={(event) =>
                                                    handlePickupLocationChange(index, 'address', event.target.value)
                                                }
                                                placeholder="Dirección del punto de retiro"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId={`pickup-city-${locationKey}`}>
                                            <Form.Label className="fw-bold small text-muted text-uppercase" column="sm">
                                                Ciudad
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                list="pickup-canton-names"
                                                value={location.city}
                                                onChange={(event) =>
                                                    handlePickupLocationChange(index, 'city', event.target.value)
                                                }
                                                placeholder="Cantón, por ejemplo Cuenca"
                                            />
                                            <Form.Text className="text-muted">
                                                Usa el nombre del cantón, igual que la ciudad de la empresa.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId={`pickup-state-${locationKey}`}>
                                            <Form.Label className="fw-bold small text-muted text-uppercase" column="sm">
                                                Provincia
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={location.state}
                                                onChange={(event) =>
                                                    handlePickupLocationChange(index, 'state', event.target.value)
                                                }
                                                placeholder="Provincia (opcional)"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId={`pickup-hours-${locationKey}`}>
                                            <Form.Label className="fw-bold small text-muted text-uppercase" column="sm">
                                                Horas adicionales
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                min={0}
                                                step="1"
                                                value={location.extraHours}
                                                onChange={(event) =>
                                                    handlePickupLocationChange(index, 'extraHours', event.target.value)
                                                }
                                            />
                                            <Form.Text className="text-muted">
                                                Tiempo extra que se suma al retiro en este punto.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId={`pickup-active-${locationKey}`}>
                                            <Form.Label className="fw-bold small text-muted text-uppercase d-block" column="sm">
                                                Activo
                                            </Form.Label>
                                            <div className="d-flex align-items-center border rounded px-3 py-2 bg-light">
                                                <Form.Check
                                                    type="switch"
                                                    id={`pickup-active-switch-${locationKey}`}
                                                    label={isActive ? 'Activo' : 'Inactivo'}
                                                    checked={isActive}
                                                    onChange={(event) =>
                                                        handlePickupLocationChange(index, 'isActive', event.target.checked)
                                                    }
                                                    className={isActive ? 'fw-bold mb-0' : 'text-muted mb-0'}
                                                    style={isActive ? { color: customColor } : {}}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}
                </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                    <h5 className="mb-0 d-flex align-items-center" style={{ color: customColor }}>
                        <FaClock className="me-2" /> Horarios de Atención
                    </h5>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                    <Form.Group className="mb-3">
                        <InputGroup>
                            <Form.Control
                                type="text"
                                placeholder="Ej: Lunes: 9:00 AM - 6:00 PM"
                                value={newOpeningHour}
                                onChange={(e) => setNewOpeningHour(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddOpeningHour();
                                    }
                                }}
                            />
                            <Button
                                variant="primary"
                                onClick={handleAddOpeningHour}
                                style={{ backgroundColor: customColor, borderColor: customColor }}
                            >
                                <FaPlus /> Agregar
                            </Button>
                        </InputGroup>
                    </Form.Group>

                    <ListGroup variant="flush" className="border rounded">
                        {formData.openingHours.length === 0 && (
                            <ListGroup.Item className="text-muted text-center fst-italic py-3">
                                No hay horarios registrados
                            </ListGroup.Item>
                        )}
                        {formData.openingHours.map((hour, index) => (
                            <ListGroup.Item
                                key={index}
                                className="d-flex justify-content-between align-items-center"
                            >
                                <span>{hour}</span>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="border-0"
                                    onClick={() => handleRemoveOpeningHour(index)}
                                >
                                    <FaTrash />
                                </Button>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </Card.Body>
            </Card>

            <Row>
                <Col md={12}>
                    <Card className="mb-4 shadow-sm border-0">
                        <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                            <h5
                                className="mb-0 d-flex align-items-center"
                                style={{ color: customColor }}
                            >
                                <FaTruck className="me-2" /> Política de Envío
                            </h5>
                        </Card.Header>
                        <Card.Body className="px-4 pb-4">
                            <Form.Label
                                id="shipping-policy-type-label"
                                className="fw-bold small text-muted text-uppercase"
                                column="sm"
                            >
                                Tipo de política
                            </Form.Label>
                            <div
                                className="shipping-policy-options"
                                role="radiogroup"
                                aria-labelledby="shipping-policy-type-label"
                            >
                                {SHIPPING_POLICY_OPTIONS.map((option) => {
                                    const selected = formData.shippingPolicy.type === option.value;
                                    const optionId = `shipping-policy-${option.value}`;

                                    return (
                                        <div
                                            key={option.value}
                                            className={`shipping-choice-group shipping-policy-option${
                                                selected ? ' is-selected' : ''
                                            }`}
                                        >
                                            <label className="d-flex align-items-start gap-3 mb-0 w-100">
                                                <Form.Check.Input
                                                    type="radio"
                                                    name="shippingPolicy.type"
                                                    id={optionId}
                                                    value={option.value}
                                                    checked={selected}
                                                    onChange={handleChange}
                                                    className="mt-1 flex-shrink-0"
                                                />
                                                <span className="d-block flex-grow-1">
                                                    <span className="shipping-choice-chip shipping-choice-chip--pickup">
                                                        {option.value}
                                                    </span>
                                                    <span className="shipping-policy-option-title d-block">
                                                        {option.title}
                                                    </span>
                                                    <span className="shipping-policy-option-text d-block">
                                                        {option.description}
                                                    </span>
                                                </span>
                                            </label>
                                            {selected && option.value === 'FREE_OVER_AMOUNT' && (
                                                <Form.Group
                                                    controlId="shippingPolicy.freeShippingThreshold"
                                                    className="shipping-policy-threshold"
                                                >
                                                    <Form.Label
                                                        className="fw-bold small text-muted text-uppercase"
                                                        column="sm"
                                                    >
                                                        Monto mínimo para envío gratis
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>$</InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            name="shippingPolicy.freeShippingThreshold"
                                                            value={
                                                                formData.shippingPolicy
                                                                    .freeShippingThreshold
                                                            }
                                                            onChange={handleChange}
                                                            placeholder="Ej: 50.00"
                                                            min="0"
                                                            step="0.01"
                                                            aria-describedby="shipping-policy-applicability"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="shipping-policy-note" id="shipping-policy-applicability">
                                <span className="shipping-policy-note-kicker">Alcance del envío gratis</span>
                                <p className="mb-0">
                                    El envío gratis solo aplica a los productos marcados como{' '}
                                    <strong>isFreeShippingApplicable</strong>.
                                </p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={12}>
                    <Card className="mb-4 shadow-sm border-0">
                        <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                            <h5
                                className="mb-0 d-flex align-items-center"
                                style={{ color: customColor }}
                            >
                                <FaMoneyBillWave className="me-2" /> Métodos de Pago
                            </h5>
                        </Card.Header>
                        <Card.Body className="px-4 pb-4">
                            <Form.Group controlId="paymentMethods">
                                <div className="d-flex flex-column gap-2">
                                    {[
                                        { label: 'Transferencia Bancaria', value: 'BANK_TRANSFER' },
                                        { label: 'PayPhone', value: 'PAYPHONE' },
                                    ].map((method) => (
                                        <div
                                            key={method.value}
                                            className={`p-2 border rounded ${formData.paymentMethods.includes(method.value) ? 'bg-light' : ''}`}
                                            style={
                                                formData.paymentMethods.includes(method.value)
                                                    ? { borderColor: customColor }
                                                    : {}
                                            }
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                id={`payment-${method.value}`}
                                                label={method.label}
                                                value={method.value}
                                                checked={formData.paymentMethods.includes(
                                                    method.value
                                                )}
                                                onChange={handlePaymentMethodsChange}
                                                className="user-select-none mb-0"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Form.Group>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <div
                className="d-flex justify-content-end gap-2 sticky-bottom bg-white p-3 border-top shadow-lg"
                style={{
                    zIndex: 1000,
                    margin: '0 -1rem -1rem -1rem',
                    position: 'sticky',
                    bottom: 0,
                }}
            >
                <Button variant="light" onClick={onClose} className="px-4">
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={loadingUpdate}
                    className="px-4"
                    style={{ backgroundColor: customColor, borderColor: customColor }}
                >
                    {loadingUpdate ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
            </div>
            <Modal
                show={showCropper}
                onHide={() => {
                    setShowCropper(false);
                    setRawImageSrc(null);
                }}
                size="lg"
                centered
                fullscreen="sm-down"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Recortar Imagen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {rawImageSrc && (
                        <ImageCropper
                            imageSrc={rawImageSrc}
                            onCropComplete={handleCropComplete}
                            onCancel={() => {
                                setShowCropper(false);
                                setRawImageSrc(null);
                            }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </Form>
    );
};

export default CompanyEditScreen;

